<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, nextTick, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import LabelsDataTable from './LabelsDataTable.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showDeleteConfirmationPopup = ref(false);
const addLabelRef = ref(null);
const editLabelRef = ref(null);
const selectedLabel = ref({});
const searchQuery = ref('');

const EMPTY_CELL = '-';

const records = computed(() => getters['labels/getLabels'].value);

function displayText(value) {
  if (value === null || value === undefined) return EMPTY_CELL;
  const str = String(value).trim();
  return str.length ? str : EMPTY_CELL;
}

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    { name: 'title', weight: 4 },
    'description',
    'emoji',
    'created_by_name',
    'updated_by_name',
    'source',
  ]);
});

const uiFlags = computed(() => getters['labels/getUIFlags'].value);

const deleteMessage = computed(
  () => ` ${displayText(selectedLabel.value.title)}?`
);

function openAddPopup() {
  addLabelRef.value?.open();
}

async function openEditPopup(row) {
  selectedLabel.value = row;
  await nextTick();
  editLabelRef.value?.open();
}

function openDeletePopup(row) {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = row;
}

function closeDeletePopup() {
  showDeleteConfirmationPopup.value = false;
}

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

function confirmDeletion() {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
}

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LABEL_MGMT.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('LABEL_MGMT.HEADER')"
        :description="$t('LABEL_MGMT.DESCRIPTION')"
        :link-text="$t('LABEL_MGMT.LEARN_MORE')"
        :search-placeholder="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="labels"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('LABEL_MGMT.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            icon="i-lucide-plus"
            :label="$t('LABEL_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        v-if="!records.length && !searchQuery.trim()"
        class="flex flex-col items-center justify-center px-0 py-16 text-center"
      >
        <div
          class="mb-6 flex size-24 items-center justify-center rounded-full bg-n-alpha-2 outline outline-1 outline-n-weak -outline-offset-1"
        >
          <Icon icon="i-lucide-tag" class="size-12 text-n-slate-11" />
        </div>
        <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
          {{ $t('LABEL_MGMT.EMPTY_STATE.TITLE') }}
        </h3>
        <p class="mb-8 max-w-md text-body-main text-n-slate-11">
          {{ $t('LABEL_MGMT.EMPTY_STATE.DESCRIPTION') }}
        </p>
        <Button
          icon="i-lucide-plus"
          :label="$t('LABEL_MGMT.EMPTY_STATE.BUTTON')"
          @click="openAddPopup"
        />
      </div>

      <span
        v-else-if="!filteredRecords.length && searchQuery.trim()"
        class="flex flex-1 items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('LABEL_MGMT.NO_RESULTS') }}
      </span>

      <div v-else class="flex flex-col gap-3 pt-4">
        <LabelsDataTable
          :labels="filteredRecords"
          :loading="loading"
          @edit="openEditPopup"
          @delete="openDeletePopup"
        />
      </div>
    </template>

    <AddLabel ref="addLabelRef" />

    <EditLabel
      ref="editLabelRef"
      :selected-response="selectedLabel"
      @close="selectedLabel = {}"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
